import { useCallback } from 'react'
import { ErrorCode } from '@ancon/wildcat-types'
import noop from 'lodash/noop'

import useAppStore from '../../../../../store/hooks/useAppStore'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import useAppSignOut from '../../../../auth/hooks/useAppSignOut'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import { authPendingSignOutCheckoutUpdateSelector } from '../../../../auth/store/authSelectors'
import {
  setAuthProfileSelectionModalType,
  setPendingSignOutCheckoutUpdate,
} from '../../../../auth/store/authSlice'
import { ProfileSelectionModalType } from '../../../../auth/types'
import { currentCheckoutItemsSelector } from '../../../../checkout/store/checkoutSelectors'
import { deleteCheckoutItems } from '../../../../checkout/store/checkoutThunks'
import { clientContextCompanyIdByCompanyMemberIdSelector } from '../../../../clientContext/store/clientContextSelectors'
import { clientContextSetCurrentCompanyUserId } from '../../../../clientContext/store/clientContextSlice'
import { companyPendingProfileSwitchMemberIdSelector } from '../../../../company/store/companySelectors'
import { setPendingCompanyProfileSwitchMemberId } from '../../../../company/store/companySlice'
import { isPreOrderCheckoutSelector } from '../../../../preOrder/store/preOrderSelectors'
import { deletePreOrderCheckoutItem } from '../../../../preOrder/store/preOrderThunks'
import { appErrorModalConfigSelector } from '../../../store/appSelectors'
import { InformativeError } from '../../../types'
import localStorageUtils from '../../../utils/localStorageUtils'

/**
 * Removes checkout items from the cart that are unavailable
 * due to the outlet's company override menu tree when:
 * - Signing out from a company profile
 * - Switching between a company profile and a personal profile
 */
export function useDeleteOverrideMenuConflicts() {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const appSignOut = useAppSignOut()

  const errorModalConfig = useAppSelector(appErrorModalConfigSelector)
  const isPreOrderCheckout = useAppSelector(isPreOrderCheckoutSelector)

  const pendingMemberId = useAppSelector(
    companyPendingProfileSwitchMemberIdSelector,
  )
  const isPendingSignOutCheckoutUpdate = useAppSelector(
    authPendingSignOutCheckoutUpdateSelector,
  )
  const companyId = useAppSelector(state => {
    if (isPendingSignOutCheckoutUpdate || !pendingMemberId) {
      return null
    }
    return clientContextCompanyIdByCompanyMemberIdSelector(
      state,
      pendingMemberId,
    )
  })

  const getCheckoutItemIdsByVariantIds = useCallback(
    (variantIds: string[]) => {
      const currentCheckoutItems = currentCheckoutItemsSelector(
        store.getState(),
      )
      return currentCheckoutItems.reduce<string[]>((acc, item) => {
        if (variantIds.includes(item.product.variantId)) {
          acc.push(item.id)
        }
        return acc
      }, [])
    },
    [store],
  )

  const deleteConflictingItems = useCallback(
    async (checkoutItemIds: string[]) => {
      if (isPreOrderCheckout) {
        await dispatch(
          deletePreOrderCheckoutItem({ checkoutItemIds, companyId }),
        ).unwrap()
      } else {
        await dispatch(
          deleteCheckoutItems({ checkoutItemIds, companyId }),
        ).unwrap()
      }
    },
    [dispatch, isPreOrderCheckout, companyId],
  )

  const handleSignOutFlow = useCallback(async () => {
    dispatch(clientContextSetCurrentCompanyUserId(null))
    const isSignOutSuccess = await appSignOut()

    if (isSignOutSuccess) {
      dispatch(setAuthProfileSelectionModalType(ProfileSelectionModalType.None))
    }
  }, [dispatch, appSignOut])

  const handleProfileSwitchFlow = useCallback(() => {
    if (pendingMemberId) {
      localStorageUtils.setItem<string>('companyProfileId', pendingMemberId)
      dispatch(clientContextSetCurrentCompanyUserId(pendingMemberId))
    } else {
      localStorageUtils.removeItem('companyProfileId')
      dispatch(clientContextSetCurrentCompanyUserId(null))
    }

    dispatch(setAuthProfileSelectionModalType(ProfileSelectionModalType.None))
    dispatch(setPendingSignOutCheckoutUpdate(false))
    dispatch(setPendingCompanyProfileSwitchMemberId(null))
  }, [dispatch, pendingMemberId])

  const handleDeleteOverrideMenuConflictCheckoutItems =
    useCallback(async () => {
      const overrideCheckoutErrorModalConfig =
        errorModalConfig as InformativeError<ErrorCode.CheckoutItemUnavailableDueToOverrideMenu>

      const { additionalData } = overrideCheckoutErrorModalConfig || {}
      const checkoutItemProductVariantIds =
        additionalData?.checkoutItemProductVariantIds || []

      if (checkoutItemProductVariantIds.length === 0) return

      const checkoutItemIds = getCheckoutItemIdsByVariantIds(
        checkoutItemProductVariantIds,
      )

      try {
        await deleteConflictingItems(checkoutItemIds)

        if (isPendingSignOutCheckoutUpdate) {
          await handleSignOutFlow()
        } else {
          handleProfileSwitchFlow()
        }
      } catch (error) {
        noop()
      }
    }, [
      errorModalConfig,
      getCheckoutItemIdsByVariantIds,
      deleteConflictingItems,
      isPendingSignOutCheckoutUpdate,
      handleSignOutFlow,
      handleProfileSwitchFlow,
    ])

  return handleDeleteOverrideMenuConflictCheckoutItems
}
